import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, useWindowDimensions, ActivityIndicator } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { AuthLayout } from '@/components/AuthLayout';
import { Logo } from '@/components/Logo';
import { PasswordInputWithStrength } from '@/components/PasswordInputWithStrength';
import { PasswordInput } from '@/components/PasswordInput';
import { Button } from '@/components/Button';
import { ErrorMessage } from '@/components/ErrorMessage';
import { colors } from '@/styles/colors';
import { ROUTES } from '@/utils/constants';
import { validatePasswordStrength } from '@/services/validationService';
import { validateResetToken, resetPassword } from '@/api/auth';

export default function ResetPasswordScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const { width } = useWindowDimensions();

  const uidb64 = typeof params.uidb64 === 'string' ? params.uidb64 : '';
  const token = typeof params.token === 'string' ? params.token : '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Token validation state
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [sessionToken, setSessionToken] = useState('');

  const isDesktop = width >= 768;

  // Validate password strength
  const validation = validatePasswordStrength(password);

  // Check if passwords match
  const passwordsMatch = password === confirmPassword && password.length > 0 && confirmPassword.length > 0;
  const showConfirmError = confirmPassword.length > 0 && !passwordsMatch;

  // Button enabled only when password is valid and both match
  const canSubmit = validation.isValid && passwordsMatch && !loading;

  // Validate the reset token on mount
  useEffect(() => {
    if (!uidb64 || !token) {
      setValidating(false);
      setError('Invalid reset link. Missing required parameters.');
      return;
    }

    validateResetToken({ uidb64, token })
      .then((data) => {
        setTokenValid(true);
        setSessionToken(data.session_token);
      })
      .catch(() => {
        setError('This reset link is invalid or has expired. Please request a new one.');
      })
      .finally(() => {
        setValidating(false);
      });
  }, [uidb64, token]);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setLoading(true);
    setError(null);

    try {
      await resetPassword({
        uidb64,
        new_password1: password,
        new_password2: confirmPassword,
        session_token: sessionToken,
      });

      router.push(ROUTES.LOGIN);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to reset password. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while validating token
  if (validating) {
    return (
      <AuthLayout>
        <View style={styles.container}>
          <Logo />
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>Validating reset link...</Text>
          </View>
        </View>
      </AuthLayout>
    );
  }

  // Show error if token is invalid
  if (!tokenValid) {
    return (
      <AuthLayout>
        <View style={styles.container}>
          <Logo />
          <View style={styles.content}>
            <Text style={styles.heading}>Reset password</Text>
            {error && <ErrorMessage message={error} />}
            <View style={isDesktop && styles.buttonContainer}>
              <Button
                title="Request New Reset Link"
                onPress={() => router.push(ROUTES.FORGOT_PASSWORD)}
                testID="back_to_forgot_password_button"
              />
            </View>
          </View>
        </View>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <View style={styles.container}>
        <Logo />

        <View style={styles.content}>
          <Text style={styles.heading}>Reset password</Text>

          <Text style={styles.instructions}>
            A valid password must have a minimum of 8 characters and include 1 number,
            1 capital letter, and 1 special character.
          </Text>

          {error && <ErrorMessage message={error} />}

          <PasswordInputWithStrength
            label="New password"
            value={password}
            onChangeText={setPassword}
            placeholder="New password"
            showStrength={true}
            testID="new_password"
            onSubmitEditing={() => {}}
          />

          <PasswordInput
            label="Confirm New Password"
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            placeholder="Confirm New Password"
            error={showConfirmError ? "Passwords don't match" : undefined}
            testID="confirm_password"
            onSubmitEditing={handleSubmit}
          />

          <View style={isDesktop && styles.buttonContainer}>
            <Button
              title="Set Password"
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={loading}
              testID="set_password_button"
            />
          </View>
        </View>
      </View>
    </AuthLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  content: {
    width: '100%',
  },
  heading: {
    fontSize: 32,
    lineHeight: 40,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 16,
  },
  instructions: {
    fontSize: 16,
    lineHeight: 24,
    color: colors.textSecondary,
    marginBottom: 32,
  },
  buttonContainer: {
    alignItems: 'flex-start',
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: colors.textSecondary,
  },
});
